import { resolveBackendFileUrl } from '@/lib/api/config'

/**
 * The ONE decision about the logo a portal shows, and what sits behind it (spec
 * docs/superpowers/specs/2026-10-29-branding-kit-and-brand-theme-design.md, section 3.6 and rule 3). The live header, every
 * preview, the sign-in card and the sign-in preview read this answer and draw it, so a logo cannot sit on its plate in the
 * header and vanish on the card under it.
 *
 *   Dark:  the Portal's dark logo, only when `useSameLogoForBoth` is explicitly false and one is stored that is not the light
 *          logo again; else the kit's dark logo, a logo that needs nothing behind it, or the generated white copy, with nothing behind it (`tenantLogoDarkUrl`,
 *          darkLogoOf below); else the light logo on a white plate; else the wordmark.
 *   Light: the Portal's light logo, else the tenant's (the workspace's own, else its company's); else the kit's dark logo on a
 *          chip in the brand colour; else the wordmark.
 *
 * `useSameLogoForBoth` defaults to true, so true and null are seeded values and no evidence of a choice ("Seeded is not
 * configured", CLAUDE.md): only an explicit false says a stored dark logo was meant. With no kit, every answer is what the
 * portal drew before the kit existed (__tests__/portal-logo.spec.ts runs the table).
 *
 * `url: null` means the wordmark, or the sign-in card's shield.
 *
 * Display only: never write the result back into the Portal settings, which would copy the company logo in for good.
 */
export type PortalLogoBacking = 'none' | 'white-plate' | 'brand-chip'

export interface PortalLogo {
  url: string | null
  backing: PortalLogoBacking
}

export interface PortalLogos {
  light: PortalLogo
  dark: PortalLogo
}

export interface PortalLogoSources {
  portalLogoLight?: string | null
  portalLogoDark?: string | null
  useSameLogoForBoth?: boolean | null
  /** The workspace's own logo, else its company's, as stored: `tenantLogoUrl`, or a workspace's `logoUrl`. */
  tenantLogo?: string | null
  /** The logo for dark surfaces: `tenantLogoDarkUrl`, or `kitDarkLogo` below for one not saved yet. */
  kitDarkLogo?: string | null
  /** It is the generated white copy (`tenantLogoDarkIsCopy`), which shows only where white can be read. */
  kitDarkIsCopy?: boolean | null
  /** The logo sits on the page's background picture, whose lightness nobody has measured. */
  overPicture?: boolean | null
}

export const NO_PORTAL_LOGOS: PortalLogos = { light: { url: null, backing: 'none' }, dark: { url: null, backing: 'none' } }

const onItsOwn = (url: string): PortalLogo => ({ url, backing: 'none' })

export function portalLogos({ portalLogoLight, portalLogoDark, useSameLogoForBoth, tenantLogo, kitDarkLogo: kitDark }: PortalLogoSources): PortalLogos {
  const kit = resolveBackendFileUrl(kitDark) || null
  const lightLogo = portalLogoLight || resolveBackendFileUrl(tenantLogo) || null
  // One picture in both slots is no dark logo: "the same logo" ticked and unticked left a copy. The public read gives two fields
  // holding one picture the same url (linkPublicImages), so this compare holds there too.
  const portalDark = useSameLogoForBoth === false && portalLogoDark !== portalLogoLight ? portalLogoDark || null : null

  const light: PortalLogo = lightLogo ? onItsOwn(lightLogo) : kit ? { url: kit, backing: 'brand-chip' } : NO_PORTAL_LOGOS.light
  const darkLogo = portalDark ?? kit
  const dark: PortalLogo = darkLogo ? onItsOwn(darkLogo) : lightLogo ? { url: lightLogo, backing: 'white-plate' } : NO_PORTAL_LOGOS.dark
  return { light, dark }
}

/**
 * The dark surface draws a DIFFERENT picture with nothing behind it: the customer's dark logo or the generated white copy. A pale logo
 * on a dark page looks bigger than the same logo dark on a light one, so a header draws it 6% smaller (lib/brand/logo-size.ts). The
 * logo on its white plate, or one picture on both surfaces, keeps its height.
 */
export const darkLogoIsWhiteOnDark = ({ light, dark }: PortalLogos): boolean =>
  dark.url !== null && dark.backing === 'none' && dark.url !== light.url

/**
 * The sign-in card's logos: the decision, and where that leaves it with no logo for the page and a Portal dark logo is stored, that
 * logo. A customer whose only logo is a dark one uploaded under Settings -> Portal keeps it on the card rather than getting the
 * shield: on a dark card with nothing behind it, and on a light one on the chip, as rule 3's light half draws any logo made for dark
 * surfaces, because a white logo vanishes on a light card. The card's alone: the header's wordmark there has always been the header's
 * answer, and __tests__/portal-logo.spec.ts holds both tables.
 */
export function signInCardLogos(sources: PortalLogoSources): PortalLogos {
  // The copy follows the surface (spec 3.4 rule 3). A picture is a surface with no number: white may read on it or vanish, so the
  // box stays. A dark logo the customer uploaded was chosen by a person, and stays on a picture as it always did.
  const logos = portalLogos(sources.overPicture && sources.kitDarkIsCopy ? { ...sources, kitDarkLogo: null } : sources)
  const stored = sources.portalLogoDark
  if (!stored) return logos
  return {
    light: logos.light.url ? logos.light : { url: stored, backing: 'brand-chip' },
    dark: logos.dark.url ? logos.dark : onItsOwn(stored),
  }
}

/**
 * What sits behind a logo. The chip is rule 3's light half: a logo made for dark surfaces, with no other on a light one. Its
 * colour is the brand's text shade, not the fill: the engine holds that at 4.5:1 against white, and white ink on a bright
 * fill would vanish as surely as on the page. The theme builder has no brand and writes its own text shade, held the same way, or a
 * bright colour picked there would fall through to the fill. With neither it reads Classic's primary.
 *
 * These go on the image, and an image is border-box, so plain padding comes out of the logo: a header's 28 px logo drew at 20 on its
 * plate. `box-content` adds the padding to the box instead, and the negative margin gives that room back, so the logo is its own size
 * and nothing around it moves. A parent that clips shows the logo on white, edge to edge. Two consequences for a caller: a margin of
 * its own is replaced by this one, so it goes on a parent; and padding of its own goes on a frame around the logo, or sits inside this
 * box. One painted surface on purpose: a ring leaves a gap at 125% and 150% scaling (__tests__/logo-plate-keeps-the-logo-size.spec.ts).
 */
export const PORTAL_LOGO_BACKING_CLASS: Readonly<Record<PortalLogoBacking, string>> = {
  none: '',
  'white-plate': 'box-content rounded bg-white/95 p-1 -m-1',
  'brand-chip': 'box-content rounded bg-[var(--app-primary-text,var(--app-builder-text,var(--primary)))] p-1 -m-1',
}

/**
 * The logo a preview of the Portal's own page may draw for dark, while the logo on it is `draft` and the saved one `saved`. A copy
 * belongs to the logo it was made for: one chosen here and not saved has none yet, and a preview must not show another logo's. That
 * holds for a dark logo the customer uploaded as it does for a copy.
 */
export function savedDarkLogo(
  { tenantLogoDarkUrl }: { tenantLogoDarkUrl: string | null },
  draft: string | null | undefined,
  saved: string | null | undefined,
): string | null {
  return (draft ?? null) !== (saved ?? null) ? null : tenantLogoDarkUrl
}

/**
 * What Customise Login holds for "no background": a path to a file that was never shipped, so no picture is drawn. A row saved
 * from that page can store it.
 */
export const DEFAULT_LOGIN_BACKGROUND = '/user-portal-login/deafult%20login%20bg.jpg'

/** Does the sign-in page really have a background picture? */
export const isLoginPicture = (value: string | null | undefined): boolean => !!value && value !== DEFAULT_LOGIN_BACKGROUND

/**
 * Does the sign-in logo stand on that picture? The default card draws it on the page, above the card. A company's card styles draw it on
 * the card, the column's own surface or a white chip, which have numbers (white-logo-surfaces.spec.ts), so the copy shows there.
 */
export const logoStandsOnPicture = (value: string | null | undefined, onCompanyCard: boolean): boolean =>
  !onCompanyCard && isLoginPicture(value)

type KitLogo = { kind: string; url: string; ink: string; opaqueBackground?: boolean; source?: { kind: string }; unused?: string | null; needsNoPlate?: string | null; notTheKitsLogo?: boolean | null }

const FOR_DARK_FIRST: readonly string[] = ['full-dark', 'full-light', 'one-colour', 'mark']

/** The copy we made of the logo: the API files it, and a page shows it and never stores it. */
export const isGeneratedLogo = (logo: Pick<KitLogo, 'source'>): boolean => logo.source?.kind === 'generated'

/**
 * What a dark surface shows, the one decision (spec 3.4 rule 3): a dark logo the customer uploaded, then a logo that needs nothing
 * (`needsNoPlate`: a pale one, or a badge with its own background), then the generated copy, then null, which is the box. Measured
 * ink decides, never the label (rule 1). An uploaded dark logo answers only for the kit's own logo (`notTheKitsLogo` says the logo
 * shown is another). The API holds the same rule for what is saved (apps/api/src/modules/tenant-branding/logo/dark-logo.ts); both
 * run lib/portal/__tests__/fixtures/dark-logo-cases.json.
 */
export function darkLogoOf<T extends KitLogo>(logos: ReadonlyArray<T>): T | null {
  return uploadedDarkLogo(logos) ?? logos.find((logo) => logo.needsNoPlate) ?? logos.find((logo) => isGeneratedLogo(logo) && !logo.unused) ?? null
}

function uploadedDarkLogo<T extends KitLogo>(logos: ReadonlyArray<T>): T | null {
  if (logos.some((logo) => logo.notTheKitsLogo)) return null
  const own = logos.filter((logo) => !isGeneratedLogo(logo))
  const chosen = own.find((logo) => logo.kind === 'full-dark' && (logo.ink !== 'dark' || logo.opaqueBackground))
  if (chosen) return chosen
  const light = own.filter((logo) => logo.ink === 'light')
  return [...light].sort((a, b) => FOR_DARK_FIRST.indexOf(a.kind) - FOR_DARK_FIRST.indexOf(b.kind))[0] ?? null
}

/**
 * Is a Portal logo standing in front of the brand kit (spec 3.6)? It has to be one the portal really draws, over a logo of the kit's
 * the portal would otherwise draw: a dark logo stored under same-logo is drawn nowhere, and a mark or a dark-ink dark logo never was.
 */
export function portalLogoOverridesKit({ portalLogoLight, portalLogoDark, useSameLogoForBoth }: Pick<PortalLogoSources, 'portalLogoLight' | 'portalLogoDark' | 'useSameLogoForBoth'>, kitLogos: ReadonlyArray<KitLogo>): boolean {
  const kitDark = kitDarkLogo(kitLogos) !== null
  const kitLight = kitLogos.some((logo) => logo.kind === 'full-light')
  const lightOverrides = !!portalLogoLight && (kitLight || kitDark)
  const darkOverrides = useSameLogoForBoth === false && !!portalLogoDark && kitDark
  return lightOverrides || darkOverrides
}

/**
 * The kit's own logo for dark surfaces, before it is saved anywhere the public settings can read: a preview of "Your brand". Never a
 * generated copy: that was made of one logo, and only what is saved knows which logo a surface shows.
 */
export function kitDarkLogo(logos: ReadonlyArray<KitLogo>): string | null {
  const logo = darkLogoOf(logos)
  return logo && !isGeneratedLogo(logo) ? logo.url : null
}
